import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    TextField,
    MenuItem,
    Grid,
    Typography
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { format, parseISO, startOfDay, endOfDay, setHours, setMinutes, addMinutes, isBefore } from "date-fns";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const AppointmentSchema = Yup.object().shape({
    professionalId: Yup.number().required("Required"),
    subSpecialtyId: Yup.number().required("Required"),
    contactId: Yup.number().required("Required"),
    date: Yup.string().required("Required"),
    startTime: Yup.string().required("Required"),
});

const AppointmentModal = ({ open, onClose, appointmentId, initialValues, reload, scheduleSlots }) => {
    const history = useHistory();

    const [appointment, setAppointment] = useState({
        professionalId: "",
        subSpecialtyId: "",
        contactId: null,
        date: format(new Date(), "yyyy-MM-dd"),
        startTime: "08:00",
        patientName: "",
        status: "CONFIRMED",
        notes: ""
    });

    const [professionals, setProfessionals] = useState([]);
    const [services, setServices] = useState([]);
    const [contacts, setContacts] = useState([]);

    // Quick Contact Create
    const [newContactName, setNewContactName] = useState("");
    const [newContactPhone, setNewContactPhone] = useState("");
    const [creatingContact, setCreatingContact] = useState(false);

    const [paramConfirmationOpen, setParamConfirmationOpen] = useState(false);
    const [pendingValues, setPendingValues] = useState(null);

    // Loading state for conversation button
    const [startingConversation, setStartingConversation] = useState(false);

    // Handle Start Conversation - Creates or opens ticket
    const handleStartConversation = async () => {
        if (!initialValues || !initialValues.contactId) return;

        setStartingConversation(true);
        try {
            // Create new ticket (backend will check for existing open tickets)
            const { data: ticket } = await api.post("/tickets", {
                contactId: initialValues.contactId,
                status: "open",
            });
            history.push(`/tickets/${ticket.id}`);
            handleClose();
        } catch (err) {
            toastError(err);
        } finally {
            setStartingConversation(false);
        }
    };

    useEffect(() => {
        if (initialValues) {
            setAppointment(prev => ({
                ...prev,
                ...initialValues,
                date: initialValues.date && initialValues.date.includes("T") ? format(parseISO(initialValues.date), "yyyy-MM-dd") : initialValues.date,
                startTime: initialValues.startTime ? initialValues.startTime.substring(0, 5) : "08:00",
                contactId: initialValues.contactId || (initialValues.appointment ? initialValues.appointment.contactId : null),
                patientName: initialValues.patientName || (initialValues.appointment ? initialValues.appointment.contact : (initialValues.contact ? initialValues.contact.name : "")),
            }));

            const contactToSet = initialValues.contact || (initialValues.appointment ? initialValues.appointment.contact : null);
            if (contactToSet) {
                setContacts([contactToSet]);
            }
        }
    }, [initialValues, open]);

    const [generatedSlots, setGeneratedSlots] = useState([]);
    const [loadingSlots, setLoadingSlots] = useState(false);

    useEffect(() => {
        const fetchRefData = async () => {
            try {
                const { data: profs } = await api.get("/professionals");
                setProfessionals(profs);
                const { data: subs } = await api.get("/subspecialties");
                setServices(subs);
            } catch (err) {
                toastError(err);
            }
        };
        if (open) fetchRefData();
    }, [open]);

    // Fetch Slots when Date or Professional changes
    useEffect(() => {
        const fetchSlots = async () => {
            if (!appointment.professionalId || !appointment.date) return;
            setLoadingSlots(true);
            try {
                const { data: appointments } = await api.get("/appointments", {
                    params: {
                        startDate: format(startOfDay(parseISO(appointment.date)), "yyyy-MM-dd'T'HH:mm:ss"),
                        endDate: format(endOfDay(parseISO(appointment.date)), "yyyy-MM-dd'T'HH:mm:ss"),
                        professionalId: appointment.professionalId
                    }
                });

                // Generate Slots (Simplified Logic from Appointments/index.js)
                // Ideally this should be a shared utility or Hook
                const professional = professionals.find(p => p.id === appointment.professionalId);
                if (!professional) {
                    setGeneratedSlots([]);
                    setLoadingSlots(false);
                    return;
                }

                let interval = 30; // Default
                // Dynamic Interval Calculation
                if (professional.specialties && professional.specialties.length > 0) {
                    const durations = professional.specialties
                        .map(s => s.subSpecialty ? s.subSpecialty.duration : null)
                        .filter(d => d && d > 0);
                    if (durations.length > 0) interval = Math.min(...durations);
                }

                const dayOfWeekKey = format(parseISO(appointment.date), "EEE").toLowerCase();
                let startHour = "08:00";
                let endHour = "18:00";
                let lunchStart = "12:00";
                let lunchEnd = "13:00";

                if (professional.workingHours && professional.workingHours[dayOfWeekKey]) {
                    startHour = professional.workingHours[dayOfWeekKey].start;
                    endHour = professional.workingHours[dayOfWeekKey].end;
                }
                if (professional.lunchBreak && professional.lunchBreak[dayOfWeekKey]) {
                    lunchStart = professional.lunchBreak[dayOfWeekKey].start;
                    lunchEnd = professional.lunchBreak[dayOfWeekKey].end;
                }

                const [startH, startM] = startHour.split(":").map(Number);
                const [endH, endM] = endHour.split(":").map(Number);
                let current = setMinutes(setHours(parseISO(appointment.date), startH), startM);
                const endDay = setMinutes(setHours(parseISO(appointment.date), endH), endM);
                const now = new Date();
                const newSlots = [];

                while (current < endDay) {
                    const timeStr = format(current, "HH:mm");
                    const isLunch = (timeStr >= lunchStart && timeStr < lunchEnd);
                    const isPast = isBefore(current, now);

                    // Check if booked
                    const isBooked = appointments.find(app => {
                        // Strict check AND exclude current appointment being edited
                        if (appointment.id && app.id === appointment.id) return false;
                        return app.startTime.substring(0, 5) === timeStr; // Date is already filtered by API
                    });

                    newSlots.push({
                        time: timeStr,
                        available: !isLunch && !isBooked && !isPast,
                        label: isLunch ? "Almoço" : isBooked ? "Ocupado" : isPast ? "Passado" : "Livre"
                    });

                    current = addMinutes(current, 60); // Using 60m as requested/observed in main grid
                }
                setGeneratedSlots(newSlots);

            } catch (err) {
                toastError(err);
            } finally {
                setLoadingSlots(false);
            }
        };

        if (open && professionals.length > 0) fetchSlots();

    }, [appointment.date, appointment.professionalId, professionals, open, appointment.id]);
    // Added appointment.id to dependency to exclude itself? No, ID doesn't change.
    // However, if we change Date, we re-fetch.


    const handleSearchContacts = async (searchParam) => {
        try {
            const { data } = await api.get("/contacts", { params: { searchParam } });
            setContacts(data.contacts);
        } catch (err) {
            toastError(err);
        }
    };

    const handleCreateContact = async () => {
        if (!newContactName || !newContactPhone) return;
        try {
            const { data } = await api.post("/contacts", { name: newContactName, number: newContactPhone });
            setContacts([data]);
            setAppointment(prev => ({ ...prev, contactId: data.id, patientName: data.name }));
            setCreatingContact(false);
            toast.success("Contato criado!");
        } catch (err) {
            toastError(err);
        }
    };

    const handleClose = () => {
        onClose();
        setCreatingContact(false);
        setParamConfirmationOpen(false);
    };

    const handleSubmit = async (values, actions) => {
        // Check if editing existing appointment
        if (initialValues && initialValues.id) {
            const oldDate = initialValues.date; // Use root initialValues as that's what we mapped
            const oldTime = initialValues.startTime;

            // Compare values
            if (values.date !== oldDate || values.startTime !== oldTime) {
                setPendingValues(values);
                setParamConfirmationOpen(true);
                if (actions) actions.setSubmitting(false);
                return;
            }
        }
        await handleSaveAppointment(values);
        if (actions) actions.setSubmitting(false);
    };

    const handleSaveAppointment = async (values, mode = "update") => {
        try {
            const payload = {
                ...values,
                endTime: calculateEndTime(values.startTime, values.subSpecialtyId)
            };

            if (initialValues && initialValues.id) {
                if (mode === "new") {
                    // Create NEW appointment (keep old)
                    await api.post("/appointments", payload);
                    toast.success("Novo agendamento criado (anterior mantido)!");
                } else {
                    // Update existing (Reschedule)
                    await api.put(`/appointments/${initialValues.id}`, payload);
                    toast.success("Agendamento reagendado!");
                }
            } else {
                await api.post("/appointments", payload);
                toast.success("Agendado com sucesso!");
            }

            reload();
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    const handleCancelAppointment = async () => {
        if (!initialValues || !initialValues.id) return;
        try {
            await api.delete(`/appointments/${initialValues.id}`);
            toast.success("Agendamento cancelado!");
            reload();
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    const calculateEndTime = (start, serviceId) => {
        const service = services.find(s => s.id === serviceId);
        if (!service || !service.duration) return start;
        const [h, m] = start.split(":").map(Number);
        const date = new Date();
        date.setHours(h);
        date.setMinutes(m + service.duration);
        return format(date, "HH:mm");
    };

    // Filter Services based on Availability
    const getAvailableServices = () => {
        const currentStart = appointment.startTime;
        if (!currentStart || !scheduleSlots) return services;
        const startIndex = scheduleSlots.findIndex(s => s.time === currentStart);
        if (startIndex === -1) return services;
        let availableMinutes = 0;
        for (let i = startIndex; i < scheduleSlots.length; i++) {
            if (scheduleSlots[i].appointment && (!initialValues || initialValues.id !== scheduleSlots[i].appointment.id)) {
                break;
            }
            if (scheduleSlots[i].lunch) break;
            availableMinutes += 30; // Assuming 30m slots
        }
        return services.filter(s => s.duration <= availableMinutes);
    };

    const availableServices = getAvailableServices();

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ style: { boxShadow: '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)' } }}>
                <DialogTitle>
                    {initialValues && initialValues.id ? "Editar Agendamento" : i18n.t("appointments.modal.add")}
                </DialogTitle>
                <Formik
                    initialValues={appointment}
                    enableReinitialize={true}
                    validationSchema={AppointmentSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSubmit(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, setFieldValue, values }) => (
                        <Form>
                            <DialogContent dividers>
                                <Grid container spacing={2}>
                                    {initialValues && initialValues.id && (
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Agendado por: {initialValues.user ? initialValues.user.name : (initialValues.source || "Painel")}
                                                {" - "}
                                                <span style={{
                                                    color: initialValues.status === "CONFIRMED" ? "green" : "orange",
                                                    fontWeight: "bold"
                                                }}>
                                                    {initialValues.status === "CONFIRMED" ? "Confirmado" : "Não Confirmado"}
                                                </span>
                                            </Typography>
                                        </Grid>
                                    )}

                                    <Grid item xs={12}>
                                        {!creatingContact ? (
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <Autocomplete
                                                    style={{ flex: 1 }}
                                                    options={contacts}
                                                    getOptionLabel={(option) => option.name}
                                                    getOptionSelected={(option, value) => option.id === value.id}
                                                    value={contacts.find(c => c.id === values.contactId) || null}
                                                    onChange={(e, value) => {
                                                        setFieldValue("contactId", value ? value.id : null);
                                                        setFieldValue("patientName", value ? value.name : "");
                                                    }}
                                                    onInputChange={(e, value) => handleSearchContacts(value)}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label={i18n.t("appointments.modal.form.patient")}
                                                            variant="outlined"
                                                            error={touched.contactId && Boolean(errors.contactId)}
                                                            helperText={touched.contactId && errors.contactId}
                                                        />
                                                    )}
                                                    disabled={!!(initialValues && initialValues.id)}
                                                />
                                                {!(initialValues && initialValues.id) && (
                                                    <Button size="small" onClick={() => setCreatingContact(true)} style={{ marginLeft: 8 }}>
                                                        Novo
                                                    </Button>
                                                )}
                                            </div>
                                        ) : (
                                            <div style={{ border: '1px solid #ddd', padding: 8, borderRadius: 4 }}>
                                                <Typography variant="caption">Novo Paciente</Typography>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={6}>
                                                        <TextField
                                                            label="Nome"
                                                            size="small"
                                                            value={newContactName}
                                                            onChange={(e) => setNewContactName(e.target.value)}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <TextField
                                                            label="WhatsApp"
                                                            size="small"
                                                            value={newContactPhone}
                                                            onChange={(e) => setNewContactPhone(e.target.value)}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} style={{ textAlign: 'right' }}>
                                                        <Button size="small" onClick={() => setCreatingContact(false)}>Cancelar</Button>
                                                        <Button size="small" color="primary" onClick={handleCreateContact}>Salvar</Button>
                                                    </Grid>
                                                </Grid>
                                            </div>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            select
                                            label={i18n.t("appointments.modal.form.professional")}
                                            name="professionalId"
                                            fullWidth
                                            variant="outlined"
                                            disabled
                                        >
                                            {professionals.map((prof) => (
                                                <MenuItem key={prof.id} value={prof.id}>
                                                    {prof.name}
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            select
                                            label={i18n.t("appointments.modal.form.service")}
                                            name="subSpecialtyId"
                                            fullWidth
                                            variant="outlined"
                                            error={touched.subSpecialtyId && Boolean(errors.subSpecialtyId)}
                                        >
                                            {availableServices.map((sub) => (
                                                <MenuItem key={sub.id} value={sub.id}>
                                                    {sub.name} ({sub.duration} min)
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Field name="date">
                                            {({ field, form }) => (
                                                <TextField
                                                    {...field}
                                                    label={i18n.t("appointments.modal.form.date")}
                                                    type="date"
                                                    fullWidth
                                                    variant="outlined"
                                                    InputLabelProps={{ shrink: true }}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        form.setFieldValue("date", val);
                                                        setAppointment(prev => ({ ...prev, date: val }));
                                                    }}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            select
                                            label={i18n.t("appointments.modal.form.time")}
                                            name="startTime"
                                            fullWidth
                                            variant="outlined"
                                            error={touched.startTime && Boolean(errors.startTime)}
                                            helperText={touched.startTime && errors.startTime}
                                        >
                                            {loadingSlots ? (
                                                <MenuItem disabled>Carregando horários...</MenuItem>
                                            ) : (
                                                generatedSlots.map((slot) => (
                                                    <MenuItem key={slot.time} value={slot.time} disabled={!slot.available}>
                                                        {slot.time} {slot.label ? `(${slot.label})` : ""}
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            label="Observações"
                                            name="notes"
                                            multiline
                                            rows={3}
                                            fullWidth
                                            variant="outlined"
                                        />
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                {initialValues && initialValues.id && (
                                    <>
                                        <Button
                                            onClick={handleCancelAppointment}
                                            style={{ color: "red", marginRight: "8px" }}
                                            disabled={isSubmitting}
                                        >
                                            Desmarcar
                                        </Button>
                                        <Button
                                            onClick={handleStartConversation}
                                            style={{ marginRight: "auto" }}
                                            disabled={isSubmitting || startingConversation}
                                            variant="outlined"
                                            color="primary"
                                        >
                                            {startingConversation ? "Abrindo..." : "Conversar"}
                                        </Button>
                                    </>
                                )}
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("global.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={isSubmitting ? "circularProgress" : ""}
                                >
                                    {i18n.t("global.buttons.save")}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className="buttonProgress"
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            <Dialog open={paramConfirmationOpen} onClose={() => setParamConfirmationOpen(false)} PaperProps={{ style: { boxShadow: '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)' } }}>
                <DialogTitle>Alteração de Agendamento</DialogTitle>
                <DialogContent>
                    <Typography>
                        Você alterou a data ou horário. Deseja REAGENDAR (substituir o anterior) ou criar um NOVO AGENDAMENTO mantendo o antigo?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setParamConfirmationOpen(false)} color="secondary">
                        Cancelar
                    </Button>
                    <Button onClick={() => handleSaveAppointment(pendingValues, "update")} color="primary" variant="contained">
                        Reagendar
                    </Button>
                    <Button onClick={() => handleSaveAppointment(pendingValues, "new")} color="primary" variant="outlined">
                        Novo Agendamento
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AppointmentModal;
